import { ChangeEvent, useRef, useState } from "react";
import { Avatar, Box, IconButton, SvgIconProps } from "@mui/material";
import { v4 as UUID_V4 } from "uuid";

import { getMediaURL } from "../../api/services/users";
import { colors } from "../../theme/palette";
import { EditProfilePhoto } from "../../assets/icons/MUI/EntitiesIcons";

interface ImageUploaderProps {
  imageSrc?: string;
  altText?: string | ((props: SvgIconProps) => JSX.Element);
  disabled?: boolean;
  onUploadStart?: () => void;
  onUploadComplete?: (file: File, url: string, fileName: string) => void;
  avatarDim?: string;
  isLoading?: boolean;
}

const ImageUploader = ({
  imageSrc,
  altText,
  disabled,
  onUploadStart,
  onUploadComplete,
  avatarDim,
  isLoading,
}: ImageUploaderProps) => {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [previewIMG, setPreviewIMG] = useState<string | null>(null);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;

    const file = event.target.files[0];
    const fileName = `${UUID_V4()}.${file.name.split(".").pop()}`;

    const reader = new FileReader();
    reader.onloadend = () => setPreviewIMG(reader.result as string);
    reader.readAsDataURL(file);

    onUploadStart?.();

    try {
      const url = await getMediaURL(fileName);
      onUploadComplete?.(file, url, fileName);
    } catch (error) {
      console.error("Upload failed", error);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <Avatar style={{ width: avatarDim || "96px", height: avatarDim || "96px" }} src={previewIMG || imageSrc}>
        {typeof altText === "string" ? altText : altText && altText({})}
      </Avatar>
      <IconButton
        disabled={disabled}
        onClick={() => uploadInputRef.current?.click()}
        sx={{
          position: "absolute",
          bottom: "0",
          right: "0",
          background: colors.Green100,
          border: `1px solid ${colors.White}`,
          "&:hover": { background: colors.Green50 },
        }}
      >
        <EditProfilePhoto />
      </IconButton>
    </Box>
  );
};

export default ImageUploader;
